import { Ionicons } from '@expo/vector-icons';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';

import { LocalTypeahead } from '@/components/common/local-typeahead';
import { DateInput } from '@/components/form/date-input';
import { Button, Modal, MoneyTextInput, Text, TextInput } from '@/components/ui';
import { useBankAccounts, useCreateJournalEntry, useGlCodes } from '@/hooks/use-accounting';
import type { BankAccount, GlCode, JournalEntry, JournalLineInput } from '@/types/models';
import { Alert } from '@/utils/alert';
import { bankParentGlIdSet } from '@/utils/bank-parent-gl';
import { businessMsToDate, toBusinessMs } from '@/utils/date-utils';
import { parseMoneyToCents } from '@/utils/format-money';

export interface BankAdjustmentModalProps {
	visible: boolean;
	bank: BankAccount;
	onDismiss: () => void;
	/** Receives the created entry so callers can act on it — the reconcile page
	 *  pre-ticks the new row (#429). */
	onCreated?: (entry: JournalEntry) => void;
	/**
	 * Business-date (midnight UTC ms) to seed "Posted on" with. The reconcile
	 * page passes the statement's last day so an adjustment lands INSIDE the
	 * period being reconciled; without it the field defaulted to today, which
	 * for any past-month statement puts the entry outside the period entirely.
	 */
	defaultPostedOn?: number;
}

interface GlOption { id: string; label: string }

/**
 * Records an ad-hoc adjustment against a bank account by writing a balanced
 * 2-line journal entry: the bank's cash GL on one leg, a user-picked GL on
 * the other. Sign convention: positive amount increases the bank balance
 * (debit cash / credit counter-GL); negative decreases it.
 */
export const BankAdjustmentModal: React.FC<BankAdjustmentModalProps> = ({
	visible, bank, onDismiss, onCreated, defaultPostedOn,
}) => {
	const seedDate = useMemo(
		() => (defaultPostedOn != null ? businessMsToDate(defaultPostedOn) : new Date()),
		[defaultPostedOn],
	);
	const [postedDate, setPostedDate] = useState<Date | null>(seedDate);
	// Adopt a late-arriving default (the reconciliation loads async) as long as
	// the user hasn't already touched the field.
	const seededFor = useRef<number | undefined>(defaultPostedOn);
	useEffect(() => {
		if (seededFor.current === defaultPostedOn) return;
		seededFor.current = defaultPostedOn;
		setPostedDate(seedDate);
	}, [defaultPostedOn, seedDate]);
	const [amount, setAmount] = useState('');
	const [memo, setMemo] = useState('');
	const [counterGlId, setCounterGlId] = useState<string | undefined>();
	const create = useCreateJournalEntry();

	const { data: glResp } = useGlCodes({ limit: 1000 });
	const { data: bankResp } = useBankAccounts({ associationId: bank.associationId, limit: 200 });
	// Split-bank parents (#357): a bank GL that has sub-account bank GLs is a
	// non-postable roll-up — keep it out of the offsetting-GL picker.
	const parentGlIds = useMemo(() => {
		const bankGlIds = new Set(
			((bankResp?.data ?? []) as BankAccount[]).map((b) => b.glCodeId).filter((x): x is string => !!x),
		);
		return bankParentGlIdSet((glResp?.data ?? []) as GlCode[], bankGlIds);
	}, [glResp, bankResp]);
	const glOptions = useMemo<GlOption[]>(() => {
		const rows = (glResp?.data ?? []) as GlCode[];
		return rows
			.filter((g) => g.active && g.id !== bank.glCodeId && !parentGlIds.has(g.id))
			.map((g) => ({ id: g.id, label: `${g.code} — ${g.fullPath || g.name}` }));
	}, [glResp, bank.glCodeId, parentGlIds]);

	const reset = () => {
		setPostedDate(seedDate);
		setAmount('');
		setMemo('');
		setCounterGlId(undefined);
	};

	const onSubmit = () => {
		if (!bank.glCodeId) {
			return Alert.alert('Bank GL not linked', 'This bank account has no GL code assigned. Edit the bank account and pick a cash GL before recording adjustments.');
		}
		if (!postedDate) return Alert.alert('Missing date', 'Pick a date for the adjustment.');
		if (!counterGlId) return Alert.alert('Missing GL code', 'Pick the offsetting GL code.');
		const cents = parseMoneyToCents(amount);
		if (!Number.isFinite(cents) || cents === 0) {
			return Alert.alert('Invalid amount', 'Enter a non-zero dollar amount. Use a leading minus for withdrawals.');
		}

		// Positive amount → bank balance increases → debit cash, credit counter
		// Negative amount → bank balance decreases → credit cash, debit counter
		const abs = Math.abs(cents);
		const bankDebit = cents > 0 ? abs : 0;
		const bankCredit = cents > 0 ? 0 : abs;
		const lines: JournalLineInput[] = [
			{ sortOrder: 0, glCodeId: bank.glCodeId, bankAccountId: bank.id, debitCents: bankDebit, creditCents: bankCredit, memo: memo.trim() || undefined },
			{ sortOrder: 1, glCodeId: counterGlId, debitCents: bankCredit, creditCents: bankDebit, memo: memo.trim() || undefined },
		];

		// Post as a 'manual' journal entry (an allowed source type) with NO
		// sourceRef: the (source, source_ref) pair drives postLedgerEntry's
		// idempotency check, so reusing bank.id would make a SECOND adjustment to
		// the same bank silently no-op. Leaving sourceRef unset keeps each
		// adjustment an independent entry. The bank is identified via the line's
		// bankAccountId + the memo instead.
		create.mutate({
			associationId: bank.associationId,
			// toBusinessMs, NOT local midnight: DateInput carries the picked day in
			// the date's UTC fields, so a local-getter conversion lands 4–5h past
			// UTC midnight here — enough to fail an inclusive `<= periodEnd` bound
			// and drop the adjustment out of the reconciliation period entirely.
			postedOn: toBusinessMs(postedDate),
			memo: memo.trim() || undefined,
			source: 'manual',
			lines,
		}, {
			onSuccess: (entry) => {
				reset();
				onCreated?.(entry);
				onDismiss();
			},
			onError: (e) => Alert.alert('Could not record adjustment', e.message),
		});
	};

	return (
		<Modal visible={visible} onDismiss={onDismiss} onSubmit={onSubmit} contentContainerStyle={styles.modal}>
			<View style={styles.header}>
				<Text style={styles.title}>Add adjustment</Text>
				<Pressable onPress={onDismiss} hitSlop={8}>
					<Ionicons name="close" size={22} color="#78909c" />
				</Pressable>
			</View>
			<Text style={styles.help}>
				Records a balanced journal entry against {bank.name}. Use a positive amount to
				increase the balance (e.g. interest earned) or a negative amount to decrease it
				(e.g. service fee).
			</Text>

			<View style={styles.field}>
				<DateInput label="Posted on *" value={postedDate} onChange={setPostedDate} />
			</View>

			<MoneyTextInput
				testID="adjustment-amount-input"
				mode="outlined"
				label="Amount * (negative for withdrawals)"
				value={amount}
				onChangeText={setAmount}
				placeholder="0.00"
				style={styles.field}
			/>

			<View style={styles.field}>
				<Text style={styles.fieldLabel}>Offsetting GL code *</Text>
				<LocalTypeahead<GlOption>
					testID="adjustment-gl-code-typeahead"
					items={glOptions}
					toOption={(o) => ({ id: o.id, label: o.label })}
					value={counterGlId}
					onChange={(id) => setCounterGlId(id ?? undefined)}
					placeholder="Search GL codes…"
				/>
			</View>

			<TextInput
				mode="outlined"
				label="Memo"
				value={memo}
				onChangeText={setMemo}
				multiline
				numberOfLines={2}
				style={styles.field}
			/>

			<View style={styles.actions}>
				<Button testID="adjustment-cancel-btn" mode="outlined" onPress={onDismiss} disabled={create.isPending}>Cancel</Button>
				<Button testID="adjustment-submit-btn" mode="contained" onPress={onSubmit} disabled={create.isPending}>
					{create.isPending ? 'Recording…' : 'Record adjustment'}
				</Button>
			</View>
		</Modal>
	);
};

const styles = StyleSheet.create({
	modal: { width: 520, maxWidth: '95%' },
	header: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 },
	title: { fontSize: 18, fontWeight: '700', color: '#212121' },
	help: { color: '#78909c', fontSize: 13, marginBottom: 12 },
	field: { marginBottom: 12 },
	fieldLabel: { color: '#546e7a', fontSize: 12, fontWeight: '600', marginBottom: 4 },
	actions: { flexDirection: 'row', justifyContent: 'flex-end', gap: 8, marginTop: 8 },
});
